import { PersonOutline, ShieldOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Stack,
  Typography
} from "@mui/material";
import { COLORS, disabledContainedButtonSx } from "assets";
import { MyModal, TextInput } from "components";
import { ENDPOINTS } from "config/constants";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { useSnackbar } from "notistack";
import { MouseEventHandler, useCallback, useEffect, useState } from "react";
import { IMembers } from "types";

interface IRemoveMember {
  member: IMembers;
  onSuccess?: () => void;
  onClose: () => void;
}

const fieldSx = { height: 48, minHeight: 48 };
const iconSx = { fontSize: 20, color: COLORS.surface.secondaryText };

const cancelSx = {
  minWidth: 120,
  height: 44,
  borderRadius: `${COLORS.radius.md}px`,
  borderColor: COLORS.surface.border,
  color: COLORS.surface.primaryText,
  fontWeight: 500,
  textTransform: "none"
};

const removeSx = {
  minWidth: 140,
  height: 44,
  borderRadius: `${COLORS.radius.md}px`,
  fontWeight: 500,
  textTransform: "none",
  bgcolor: COLORS.error,
  color: COLORS.white,
  boxShadow: "none",
  "&:hover": {
    bgcolor: COLORS.error,
    filter: "brightness(1.05)",
    boxShadow: "none"
  },
  "&.Mui-disabled": disabledContainedButtonSx
};

export default function RemoveMember({ onSuccess, onClose, member }: IRemoveMember) {
  const [checked, setChecked] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const t = useTranslation();
  const { isLoading, response, error, callFetch: callFetchPost } = useFetch("DELETE");
  const onSubmit: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      e.preventDefault();
      callFetchPost(ENDPOINTS.members + "/" + member._id).catch(console.error);
    },
    [member, callFetchPost]
  );
  useEffect(() => {
    if (response) {
      enqueueSnackbar(response.message || "", { variant: "success" });
      onClose();
      onSuccess?.();
    }
  }, [response, onSuccess, onClose, enqueueSnackbar]);
  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message || "", { variant: "error" });
    }
  }, [error, enqueueSnackbar]);

  return (
    <MyModal
      title={t["Remove Member"]}
      subtitle={t["Are You Sure You Want to Remove?"]}
      open={true}
      setOpen={onClose}
      variant="light"
    >
      <Stack spacing={2.5}>
        <TextInput
          value={member.user?.name || ""}
          label={t["Name"]}
          type="text"
          disabled
          startAdornment={<PersonOutline sx={iconSx} />}
          sx={fieldSx}
          fullWidth
        />
        <TextInput
          value={member.user?.email || ""}
          label={t["Email"]}
          type="email"
          disabled
          sx={fieldSx}
          fullWidth
        />
        <TextInput
          value={member?.role || ""}
          label={t["Role"]}
          type="text"
          disabled
          startAdornment={<ShieldOutlined sx={iconSx} />}
          sx={fieldSx}
          fullWidth
        />

        <FormControl fullWidth>
          <FormControlLabel
            control={
              <Checkbox
                checked={checked}
                onChange={(_, value) => setChecked(value)}
                sx={{
                  color: COLORS.surface.secondaryText,
                  "&.Mui-checked": { color: COLORS.error }
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: 14, color: COLORS.surface.secondaryText, lineHeight: 1.5 }}>
                {t["I confirm that removing this is an irreversible action and i want to proceed."]}
              </Typography>
            }
          />
        </FormControl>
      </Stack>

      <Box display="flex" justifyContent="flex-end" gap={1.5} mt={3}>
        <Button type="button" variant="outlined" onClick={onClose} disabled={isLoading} sx={cancelSx}>
          {t["Cancel"]}
        </Button>
        <Button
          type="button"
          variant="contained"
          disabled={!checked || isLoading}
          endIcon={isLoading ? <CircularProgress size={18} /> : undefined}
          onClick={onSubmit}
          sx={removeSx}
        >
          {t["Remove"]}
        </Button>
      </Box>
    </MyModal>
  );
}
